import React, {useState, useEffect} from "react";
import {useGetTicketByUniqueCode} from "../../hooks/Administrative/useGetTicketByUniqueCode";
import "./TicketInfo.css";
import {format} from "date-fns";
import {es} from "date-fns/locale/es";
import {useUpdateTicketState} from "../../hooks/Administrative/useUpdateTicketState";
import {Bounce, toast} from "react-toastify";
import {useDownloadTicketFiles} from "../../hooks/Administrative/useDownloadTicketFiles";
import {useAuthContext} from "../../context/AuthProvider";
import {BudgetDto} from "../../services/Supervisor/ticketService";
import {useAddBudget} from "../../hooks/Supervisor/useAddBudget";

const ticketStateMap: Map<string, string> = new Map([
    ["CREATED", "Creado"],
    ["APPROVED", "Aprobado"],
    ["ASSIGNED", "Asignado"],
    ["IN_PROGRESS", "En progreso"],
    ["BLOCKED", "Bloqueado"],
    ["IN_REVIEW", "En revisión"],
    ["COMPLETED", "Completado"],
]);

const serviceTypeMap: Map<string, string> = new Map([
    ["PREVENTIVE", "Preventivo"],
    ["CORRECTIVE", "Correctivo"],
    ["INSTALLATION", "Instalación"],
    ["VERIFICATION", "Verificación"],
]);

const TicketInfo = ({additionalInfo, ticketCode, onBack, showTicketInfo}: {
    additionalInfo: any;
    ticketCode: string;
    onBack: () => void;
    showTicketInfo: (show: boolean) => void;
}) => {
    const {ticket, updateTicketBudgetLocally} = useGetTicketByUniqueCode(ticketCode);
    const {role} = useAuthContext();
    const [isModalVisible, setModalVisible] = useState(false);
    const [isCancelModalVisible, setCancelModalVisible] = useState(false);
    const [isBudgetModalVisible, setBudgetModalVisible] = useState(false);
    const [cancelReason, setCancelReason] = useState("");
    const updateState = useUpdateTicketState()
    const {filesToDownload, getFiles} = useDownloadTicketFiles();
    const {addBudget, addingBudget} = useAddBudget();

    // Budget modal states
    const [serviceCost, setServiceCost] = useState<string>("");
    const [materialsRefacciones, setMaterialsRefacciones] = useState<{name: string, cost: string}[]>([{name: "", cost: ""}]);
    const [equipmentCosts, setEquipmentCosts] = useState<{[key: string]: string}>({});
    const [viaticos, setViaticos] = useState<string>("");

    const formattedDate = ticket?.createdAt
        ? format(new Date(ticket.createdAt.replace(" ", "T")), "dd MMMM yyyy, HH:mm:ss", {locale: es})
        : "N/A";

    useEffect(() => {
        if (ticket?.budget && ticket.budget.length > 0) {
            // Prellenar servicio
            const serviceItem = ticket.budget.find((item: BudgetDto) => item.conceptType === 'SERVICE');
            if (serviceItem) {
                setServiceCost(serviceItem.amount.toString());
            }

            // Prellenar materiales/refacciones
            const materials = ticket.budget.filter((item: BudgetDto) => item.conceptType === 'MATERIAL');
            if (materials.length > 0) {
                setMaterialsRefacciones(materials.map((item: BudgetDto) => ({
                    name: item.description,
                    cost: item.amount.toString()
                })));
            }

            // Prellenar equipos
            const equipments = ticket.budget.filter((item: BudgetDto) => item.conceptType === 'EQUIPMENT');
            if (equipments.length > 0) {
                const equipmentCostsObj: {[key: string]: string} = {};
                equipments.forEach((item: BudgetDto) => {
                    equipmentCostsObj[item.description] = item.amount.toString();
                });
                setEquipmentCosts(equipmentCostsObj);
            }

            // Prellenar viáticos
            const travelExpenses = ticket.budget.find((item: BudgetDto) => item.conceptType === 'TRAVEL_EXPENSES');
            if (travelExpenses) {
                setViaticos(travelExpenses.amount.toString());
            }
        }
    }, [ticket?.budget]);

    const handleAcceptTicket = () => {
        setModalVisible(true);
    };

    const handleCancelTicket = () => {
        setCancelModalVisible(true);
    };

    const closeModal = () => {
        setModalVisible(false);
        setCancelModalVisible(false);
        setCancelReason('')
    };

    const handleViewBudget = () => {
        setBudgetModalVisible(true);
    };

    const closeBudgetModal = () => {
        setBudgetModalVisible(false);
    };

    const addMaterialRefaccion = () => {
        setMaterialsRefacciones([...materialsRefacciones, {name: "", cost: ""}]);
    };

    const removeMaterialRefaccion = (index: number) => {
        setMaterialsRefacciones(materialsRefacciones.filter((_, i) => i !== index));
    };

    const updateMaterialRefaccion = (index: number, field: "name" | "cost", value: string) => {
        const updated = [...materialsRefacciones];
        updated[index][field] = value;
        setMaterialsRefacciones(updated);
    };

    const updateEquipmentCost = (equipment: string, cost: string) => {
        setEquipmentCosts({...equipmentCosts, [equipment]: cost});
    };

    const calculateTotalCost = () => {
        if (role === 'supervisor') {
            const service = parseFloat(serviceCost) || 0;
            const materialsTotal = materialsRefacciones.reduce((sum, item) => sum + (parseFloat(item.cost) || 0), 0);
            const equipmentsTotal = Object.values(equipmentCosts).reduce((sum, cost) => sum + (parseFloat(cost) || 0), 0);
            const viaticosTotal = parseFloat(viaticos) || 0;
            return service + materialsTotal + equipmentsTotal + viaticosTotal;
        }
        if (!ticket?.budget) return 0;
        return ticket.budget.reduce((sum: number, item: BudgetDto) => sum + item.amount, 0);
    };

    const confirmBudget = async () => {
        const budgetList: BudgetDto[] = [];

        // Agregar servicio
        if (serviceCost && parseFloat(serviceCost) > 0) {
            budgetList.push({
                conceptType: 'SERVICE',
                amount: parseFloat(serviceCost),
                description: 'Servicio'
            });
        }

        // Agregar materiales/refacciones
        materialsRefacciones.forEach(item => {
            if (item.name.trim() && item.cost && parseFloat(item.cost) > 0) {
                budgetList.push({
                    conceptType: 'MATERIAL',
                    amount: parseFloat(item.cost),
                    description: item.name
                });
            }
        });

        // Agregar equipos
        Object.entries(equipmentCosts).forEach(([equipment, cost]) => {
            if (cost && parseFloat(cost) > 0) {
                budgetList.push({
                    conceptType: 'EQUIPMENT',
                    amount: parseFloat(cost),
                    description: equipment
                });
            }
        });

        // Agregar viáticos
        if (viaticos && parseFloat(viaticos) > 0) {
            budgetList.push({
                conceptType: 'TRAVEL_EXPENSES',
                amount: parseFloat(viaticos),
                description: 'Viáticos'
            });
        }

        try {
            await addBudget(ticketCode, budgetList);
            // Actualizar el presupuesto localmente
            updateTicketBudgetLocally(budgetList);
            closeBudgetModal();
            // Limpiar los campos
            setServiceCost("");
            setMaterialsRefacciones([{name: "", cost: ""}]);
            setEquipmentCosts({});
            setViaticos("");
        } catch (error) {
            console.error('Error al agregar presupuesto:', error);
        }
    };

    const confirmAcceptTicket = () => {
        updateState.updateState("Aprobado", ticketCode, '').then((response) => {
            toast.success("Ticket aprobado", {
                position: "top-right",
                autoClose: 4000,
                hideProgressBar: false,
                closeOnClick: true,
                pauseOnHover: true,
                draggable: false,
                theme: "colored",
                transition: Bounce,
            })
        }).catch((error) => {
            toast.error(error.message, {
                position: "top-right",
                autoClose: 4000,
                hideProgressBar: false,
                closeOnClick: true,
                pauseOnHover: true,
                draggable: false,
                theme: "colored",
                transition: Bounce,
            })
        });
        ticket.ticketState = "APPROVED"
        additionalInfo['ESTADO'] = 'Aprobado'
        setModalVisible(false);
    };


    const confirmCancelTicket = () => {
        updateState.updateState("Cancelado", ticketCode, cancelReason).then((response) => {
            toast.success("Ticket rechazado", {
                position: "top-right",
                autoClose: 4000,
                hideProgressBar: false,
                closeOnClick: true,
                pauseOnHover: true,
                draggable: false,
                theme: "colored",
                transition: Bounce,
            })
        }).catch((error) => {
            toast.error(error.message, {
                position: "top-right",
                autoClose: 4000,
                hideProgressBar: false,
                closeOnClick: true,
                pauseOnHover: true,
                draggable: false,
                theme: "colored",
                transition: Bounce,
            })
        });
        setModalVisible(false);
        setCancelModalVisible(false);
        showTicketInfo(false);
        additionalInfo['ESTADO'] = 'Rechazado'
    };

    const downloadTicketFiles = () => {
        getFiles(ticketCode).then(response => {
            if (!response.files || response.files.length === 0) {
                console.log(response.files);
                toast.warning(
                    'No hay archivos encontrados para ticket',
                    {
                        position: 'top-right',
                        autoClose: 5000,
                        hideProgressBar: false,
                        closeOnClick: true,
                        pauseOnHover: true,
                        draggable: false,
                        theme: 'colored',
                        transition: Bounce,
                    }
                );
                return;
            }

            response.files.forEach((fileUrl: string, index: number) => {
                downloadFile(fileUrl, `${ticketCode}-${index + 1}`);
            });
        })
            .catch((error) => {
                toast.error("Error al obtener archivos para descargar", {
                    position: "top-right",
                    autoClose: 4000,
                    hideProgressBar: false,
                    closeOnClick: true,
                    pauseOnHover: true,
                    draggable: false,
                    theme: "colored",
                    transition: Bounce,
                });
            });
    };


    const downloadFile = async (presignedUrl: string, fileName: string) => {
        try {
            const response = await fetch(presignedUrl);

            if (!response.ok) {
                toast.error("Error al descargar archivo", {
                    position: "top-right",
                    autoClose: 4000,
                    hideProgressBar: false,
                    closeOnClick: true,
                    pauseOnHover: true,
                    draggable: false,
                    theme: "colored",
                    transition: Bounce,
                })
            }

            const blob = await response.blob();
            const url = window.URL.createObjectURL(blob);

            const a = document.createElement("a");
            a.href = url;
            a.download = fileName || "download";
            document.body.appendChild(a);
            a.click();

            document.body.removeChild(a);
            window.URL.revokeObjectURL(url);
        } catch (error) {
            toast.error("Error al buscar archivo para descargar", {
                position: "top-right",
                autoClose: 4000,
                hideProgressBar: false,
                closeOnClick: true,
                pauseOnHover: true,
                draggable: false,
                theme: "colored",
                transition: Bounce,
            })
        }
    };

    return (
        <div className="tickets-info-view-container">
            <div className="tickets-info-view-header">
                <button onClick={onBack}>REGRESAR</button>
                <div className="tickets-info-view-header-title">
                    <p>TICKET</p>
                </div>
                {ticket?.ticketState === "CREATED" && (
                    <>
                        <span className="material-icons-outlined icon-unselected" onClick={handleAcceptTicket}>
                            check
                        </span>
                        <span className="material-icons-outlined icon-unselected-cancel" onClick={handleCancelTicket}>
                            cancel
                        </span>
                    </>
                )}
                <span className="material-icons-outlined icon-unselected" onClick={handleViewBudget}>
                        attach_money
                </span>
                {ticket?.ticketState === "COMPLETED" && (
                    <span className="material-icons-outlined icon-unselected" onClick={downloadTicketFiles}>
                        download_for_offline
                    </span>
                )}
            </div>
            <div className="admin-tickets-info-wrapper">
                <div className="admin-tickets-info-view-body-info">
                    <div className="ticket-info-attribute">
                        <div className="attribute-label">CLAVE DE TICKET</div>
                        <div className="value-label">{ticketCode}</div>
                    </div>
                    <div className="ticket-info-attribute">
                        <div className="attribute-label">TIPO DE SERVICIO</div>
                        <div className="value-label">{serviceTypeMap.get(ticket?.service)}</div>
                    </div>
                    <div className="ticket-info-attribute">
                        <div className="attribute-label">ESTADO</div>
                        <div className="value-label">{ticketStateMap.get(ticket?.ticketState)}</div>
                    </div>
                    <div className="ticket-info-attribute">
                        <div className="attribute-label">DESCRIPCION</div>
                        <div className="value-label">{ticket?.description}</div>
                    </div>
                    <div className="ticket-info-attribute">
                        <div className="attribute-label">COMENTARIO ADICIONAL</div>
                        <div className="value-label">{ticket?.additionalComment}</div>
                    </div>
                    <div className="ticket-info-attribute">
                        <div className="attribute-label">TÉCNICO ASIGNADO</div>
                        <div className="value-label">{ticket?.assignedTechnician}</div>
                    </div>
                    <div className="ticket-info-attribute">
                        <div className="attribute-label">FECHA DE CREACIÓN</div>
                        <div className="value-label">{formattedDate}</div>
                    </div>
                    <div className="ticket-info-attribute">
                        <div className="attribute-label">CLIENTE</div>
                        <div className="value-label">{additionalInfo.CLIENTE}</div>
                    </div>
                    <div className="ticket-info-attribute">
                        <div className="attribute-label">PLANTA</div>
                        <div className="value-label">{additionalInfo.PLANTA}</div>
                    </div>
                    <div className="ticket-info-attribute">
                        <div className="attribute-label">FECHA DE SERVICIO</div>
                        <div className="value-label">{ticket?.serviceDate}</div>
                    </div>
                </div>
                <div className="admin-tickets-additional-info">
                    <div className="ticket-additional-info-attribute">
                        <div className="attribute-label">EQUIPOS</div>
                        <div>
                            <ul className="additional-info-attribute-list">
                                {ticket?.equipments?.map((equipment: any, index: any) => (
                                    <li key={index}>
                                        {equipment}
                                    </li>))}
                            </ul>
                        </div>
                    </div>
                </div>
            </div>

            {isModalVisible && (
                <div className="modal-overlay">
                    <div className="modal">
                        <p>¿Está seguro de que desea aceptar este ticket?</p>
                        <div className="modal-buttons">
                            <button onClick={confirmAcceptTicket}>Aceptar</button>
                            <button onClick={closeModal}>Cancelar</button>
                        </div>
                    </div>
                </div>
            )}

            {isCancelModalVisible && (
                <div className="modal-overlay">
                    <div className="modal">
                        <p>¿Está seguro de que desea rechazar este ticket?</p>
                        <p>Una vez rechazado, no se podrá interactuar con él más, y se borrará del sistema.</p>
                        <textarea
                            placeholder="Razón de cancelación (opcional)"
                            value={cancelReason}
                            onChange={(e) => setCancelReason(e.target.value)}
                            rows={4}
                        />
                        <div className="modal-buttons">
                            <button onClick={confirmCancelTicket}>Confirmar Cancelación</button>
                            <button onClick={closeModal}>Cerrar</button>
                        </div>
                    </div>
                </div>
            )}

            {isBudgetModalVisible && role === 'supervisor' && (
                <div className="modal-overlay">
                    <div className="modal budget-modal">
                        <p style={{fontWeight: "bold", fontSize: "18px"}}>Agregar Presupuesto</p>

                        <div style={{maxHeight: "60vh", overflowY: "auto", padding: "10px"}}>
                            {/* Servicio */}
                            <div style={{marginBottom: "20px"}}>
                                <label htmlFor="serviceCost" style={{display: "block", marginBottom: "5px", fontWeight: "600"}}>
                                    Servicio
                                </label>
                                <input
                                    type="number"
                                    id="serviceCost"
                                    value={serviceCost}
                                    onChange={(e) => setServiceCost(e.target.value)}
                                    placeholder="Costo del servicio"
                                    style={{
                                        border: "1px solid black",
                                        backgroundColor: "white",
                                        padding: "8px",
                                        width: "100%",
                                        borderRadius: "4px"
                                    }}
                                />
                            </div>

                            {/* Materiales/Refacciones */}
                            <div style={{marginBottom: "20px"}}>
                                <label style={{display: "block", marginBottom: "5px", fontWeight: "600"}}>
                                    Materiales/Refacciones
                                </label>
                                {materialsRefacciones.map((item, index) => (
                                    <div key={index} style={{display: "flex", gap: "10px", marginBottom: "10px", alignItems: "center"}}>
                                        <input
                                            type="text"
                                            value={item.name}
                                            onChange={(e) => updateMaterialRefaccion(index, "name", e.target.value)}
                                            placeholder="Nombre del material/refacción"
                                            style={{
                                                border: "1px solid black",
                                                backgroundColor: "white",
                                                padding: "8px",
                                                flex: "1",
                                                borderRadius: "4px"
                                            }}
                                        />
                                        <input
                                            type="number"
                                            value={item.cost}
                                            onChange={(e) => updateMaterialRefaccion(index, "cost", e.target.value)}
                                            placeholder="Costo"
                                            style={{
                                                border: "1px solid black",
                                                backgroundColor: "white",
                                                padding: "8px",
                                                width: "120px",
                                                borderRadius: "4px"
                                            }}
                                        />
                                        {materialsRefacciones.length > 1 && (
                                            <button
                                                onClick={() => removeMaterialRefaccion(index)}
                                                style={{
                                                    backgroundColor: "#f44336",
                                                    color: "white",
                                                    border: "none",
                                                    borderRadius: "4px",
                                                    padding: "8px",
                                                    cursor: "pointer",
                                                    fontSize: "12px"
                                                }}
                                            >
                                                Eliminar
                                            </button>
                                        )}
                                    </div>
                                ))}
                                <button
                                    onClick={addMaterialRefaccion}
                                    style={{
                                        backgroundColor: "#4CAF50",
                                        color: "white",
                                        border: "none",
                                        borderRadius: "4px",
                                        padding: "8px 16px",
                                        cursor: "pointer",
                                        fontSize: "12px",
                                        marginTop: "5px"
                                    }}
                                >
                                    + Agregar Material/Refacción
                                </button>
                            </div>

                            {/* Equipos */}
                            <div style={{marginBottom: "20px"}}>
                                <label style={{display: "block", marginBottom: "5px", fontWeight: "600"}}>
                                    Equipos
                                </label>
                                {ticket?.equipments?.map((equipment: string, index: number) => (
                                    <div key={index} style={{display: "flex", gap: "10px", marginBottom: "10px", alignItems: "center"}}>
                                        <span style={{
                                            border: "1px solid black",
                                            backgroundColor: "#f0f0f0",
                                            padding: "8px",
                                            flex: "1",
                                            borderRadius: "4px"
                                        }}>
                                            {equipment}
                                        </span>
                                        <input
                                            type="number"
                                            value={equipmentCosts[equipment] || ""}
                                            onChange={(e) => updateEquipmentCost(equipment, e.target.value)}
                                            placeholder="Costo"
                                            style={{
                                                border: "1px solid black",
                                                backgroundColor: "white",
                                                padding: "8px",
                                                width: "120px",
                                                borderRadius: "4px"
                                            }}
                                        />
                                    </div>
                                ))}
                            </div>

                            {/* Viáticos */}
                            <div style={{marginBottom: "20px"}}>
                                <label htmlFor="viaticos" style={{display: "block", marginBottom: "5px", fontWeight: "600"}}>
                                    Viáticos
                                </label>
                                <input
                                    type="number"
                                    id="viaticos"
                                    value={viaticos}
                                    onChange={(e) => setViaticos(e.target.value)}
                                    placeholder="Costo de viáticos"
                                    style={{
                                        border: "1px solid black",
                                        backgroundColor: "white",
                                        padding: "8px",
                                        width: "100%",
                                        borderRadius: "4px"
                                    }}
                                />
                            </div>

                            {/* Total */}
                            <div style={{
                                marginTop: "20px",
                                padding: "15px",
                                backgroundColor: "#f0f0f0",
                                borderRadius: "4px",
                                border: "2px solid var(--primary-color)"
                            }}>
                                <label style={{display: "block", marginBottom: "5px", fontWeight: "700", fontSize: "16px"}}>
                                    Costo Total
                                </label>
                                <div style={{
                                    fontSize: "24px",
                                    fontWeight: "bold",
                                    color: "var(--primary-color)"
                                }}>
                                    ${calculateTotalCost().toFixed(2)}
                                </div>
                            </div>
                        </div>

                        <div className="modal-buttons">
                            <button
                                onClick={confirmBudget}
                                disabled={addingBudget}
                                className={addingBudget ? "disabled-button" : ""}
                            >
                                Aceptar
                            </button>
                            <button
                                onClick={closeBudgetModal}
                                disabled={addingBudget}
                                className={addingBudget ? "disabled-button" : ""}
                            >
                                Cancelar
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {isBudgetModalVisible && role !== 'supervisor' && (
                <div className="modal-overlay">
                    <div className="modal budget-modal">
                        <p style={{fontWeight: "bold", fontSize: "18px"}}>Presupuesto</p>

                        <div style={{maxHeight: "60vh", overflowY: "auto", padding: "10px"}}>
                            {/* Servicio */}
                            {ticket?.budget?.filter((item: BudgetDto) => item.conceptType === 'SERVICE').map((item: BudgetDto, index: number) => (
                                <div key={index} style={{marginBottom: "20px"}}>
                                    <label style={{display: "block", marginBottom: "5px", fontWeight: "600"}}>
                                        Servicio
                                    </label>
                                    <div style={{
                                        border: "1px solid black",
                                        backgroundColor: "#f0f0f0",
                                        padding: "8px",
                                        borderRadius: "4px"
                                    }}>
                                        ${item.amount.toFixed(2)}
                                    </div>
                                </div>
                            ))}

                            {/* Materiales/Refacciones */}
                            {ticket?.budget?.filter((item: BudgetDto) => item.conceptType === 'MATERIAL').length > 0 && (
                                <div style={{marginBottom: "20px"}}>
                                    <label style={{display: "block", marginBottom: "5px", fontWeight: "600"}}>
                                        Materiales/Refacciones
                                    </label>
                                    {ticket.budget.filter((item: BudgetDto) => item.conceptType === 'MATERIAL').map((item: BudgetDto, index: number) => (
                                        <div key={index} style={{display: "flex", gap: "10px", marginBottom: "10px", alignItems: "center"}}>
                                            <div style={{
                                                border: "1px solid black",
                                                backgroundColor: "#f0f0f0",
                                                padding: "8px",
                                                flex: "1",
                                                borderRadius: "4px"
                                            }}>
                                                {item.description}
                                            </div>
                                            <div style={{
                                                border: "1px solid black",
                                                backgroundColor: "#f0f0f0",
                                                padding: "8px",
                                                width: "120px",
                                                borderRadius: "4px"
                                            }}>
                                                ${item.amount.toFixed(2)}
                                            </div>
                                        </div>
                                    ))}
                                </div>
                            )}

                            {/* Equipos */}
                            {ticket?.budget?.filter((item: BudgetDto) => item.conceptType === 'EQUIPMENT').length > 0 && (
                                <div style={{marginBottom: "20px"}}>
                                    <label style={{display: "block", marginBottom: "5px", fontWeight: "600"}}>
                                        Equipos
                                    </label>
                                    {ticket.budget.filter((item: BudgetDto) => item.conceptType === 'EQUIPMENT').map((item: BudgetDto, index: number) => (
                                        <div key={index} style={{display: "flex", gap: "10px", marginBottom: "10px", alignItems: "center"}}>
                                            <div style={{
                                                border: "1px solid black",
                                                backgroundColor: "#f0f0f0",
                                                padding: "8px",
                                                flex: "1",
                                                borderRadius: "4px"
                                            }}>
                                                {item.description}
                                            </div>
                                            <div style={{
                                                border: "1px solid black",
                                                backgroundColor: "#f0f0f0",
                                                padding: "8px",
                                                width: "120px",
                                                borderRadius: "4px"
                                            }}>
                                                ${item.amount.toFixed(2)}
                                            </div>
                                        </div>
                                    ))}
                                </div>
                            )}

                            {/* Viáticos */}
                            {ticket?.budget?.filter((item: BudgetDto) => item.conceptType === 'TRAVEL_EXPENSES').map((item: BudgetDto, index: number) => (
                                <div key={index} style={{marginBottom: "20px"}}>
                                    <label style={{display: "block", marginBottom: "5px", fontWeight: "600"}}>
                                        Viáticos
                                    </label>
                                    <div style={{
                                        border: "1px solid black",
                                        backgroundColor: "#f0f0f0",
                                        padding: "8px",
                                        borderRadius: "4px"
                                    }}>
                                        ${item.amount.toFixed(2)}
                                    </div>
                                </div>
                            ))}

                            {/* Total */}
                            <div style={{
                                marginTop: "20px",
                                padding: "15px",
                                backgroundColor: "#f0f0f0",
                                borderRadius: "4px",
                                border: "2px solid var(--primary-color)"
                            }}>
                                <label style={{display: "block", marginBottom: "5px", fontWeight: "700", fontSize: "16px"}}>
                                    Costo Total
                                </label>
                                <div style={{
                                    fontSize: "24px",
                                    fontWeight: "bold",
                                    color: "var(--primary-color)"
                                }}>
                                    ${calculateTotalCost().toFixed(2)}
                                </div>
                            </div>
                        </div>

                        <div className="modal-buttons">
                            <button onClick={closeBudgetModal}>Cerrar</button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default TicketInfo;
